/**
 * @file Shared helper for presentation modes (Slideshow, Virtual Field Trip)
 */
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import type { Page, PresentationMode, PresentationPage } from '@@/types/presentation'
import { PresentationPageType } from '@@/types/presentation'

/**
 * Reads the current `window.location.search` and merges it with optional `overrides`.
 * Overrides win on key clashes (e.g. `lang` when viewing a translated padlet).
 */
const getPresentationSearchParams = (overrides?: Record<string, string>): Record<string, string> | undefined => {
  const currentParams = new URLSearchParams(window.location.search)
  const merged: Record<string, string> = { ...Object.fromEntries(currentParams), ...overrides }

  // Preserve locale even if it was stripped from the URL by client-side navigation.
  // The <html lang> attribute is set by Rails based on the original ?locale= param.
  if (merged.locale == null) {
    const currentLocale = getHyphenatedCurrentLocale()
    if (currentLocale !== 'en') {
      merged.locale = currentLocale
    }
  }

  return Object.keys(merged).length > 0 ? merged : undefined
}

const buildPresentationLink = ({
  showLink,
  page,
  searchParams,
  mode = 'slideshow',
}: {
  showLink: string
  page: PresentationPage
  searchParams?: ConstructorParameters<typeof URLSearchParams>[0]
  mode?: PresentationMode
}): string => {
  const pageUrlSuffix = ((): string => {
    switch (page.pageType) {
      case PresentationPageType.Section:
        return `/section/${page.sectionId}`
      case PresentationPageType.Group:
        return `/group/${page.groupId}`
      case PresentationPageType.Wish:
        return `/wish/${page.wishId}`
      case PresentationPageType.Cover:
      default:
        return ''
    }
  })()

  const normalizedShowLink = showLink.replace(/\/+$/, '')
  const baseUrl = new URL(`${normalizedShowLink}/${mode}${pageUrlSuffix}`)
  if (searchParams != null) {
    for (const [key, value] of new URLSearchParams(searchParams)) {
      baseUrl.searchParams.set(key, value)
    }
  }
  return baseUrl.toString()
}

// Index of the cover page in the flat page list.
const COVER_PAGE_INDEX = 0

// Half the duration of the pinhole (iris) page transition animation.
// The full animation is close → open; each half lasts this long.
const PINHOLE_HALF_DURATION_MS = 750

/**
 * Parse the current URL to find the matching page index in the pages list.
 * Supports `/field-trip`, `/field-trip/section/:id`, `/field-trip/wish/:id`.
 * Returns COVER_PAGE_INDEX when the URL doesn't match any page.
 */
const resolvePageIndexFromUrl = (pages: Page[]): number => {
  const match = window.location.pathname.match(/\/field-trip(?:\/(section|wish)\/(\d+))?/)
  if (match == null) return COVER_PAGE_INDEX

  const [, type, id] = match
  if (type == null || id == null) return COVER_PAGE_INDEX

  const targetPageType = type === 'section' ? PresentationPageType.Section : PresentationPageType.Wish
  const index = pages.findIndex((page) => {
    if (targetPageType === PresentationPageType.Section) {
      return (
        (page.pageType === PresentationPageType.Section || page.pageType === PresentationPageType.Group) &&
        page.entityId === id
      )
    }
    return page.pageType === PresentationPageType.Wish && page.entityId === id
  })

  return index >= 0 ? index : COVER_PAGE_INDEX
}

// Convert a post's entity ID to its CID (content identifier).
const getCurrentPostCid = (entityId: string): string => `c${entityId}`

const PRESENTATION_MODE_SEGMENTS: readonly PresentationMode[] = ['field-trip', 'slideshow']

/**
 * Detect whether a pathname is a presentation-mode URL (field-trip or
 * slideshow), returning the mode or `null` for regular padlet URLs.
 *
 * Presentation URLs are of the form `/<namespace>/<slug>/<mode>(/...)`, so the
 * mode is always at the third non-empty segment. Checking the segment by name
 * alone (e.g. with a regex like `/\/field-trip$/`) would misclassify a regular
 * wish URL whose wall slug happens to be `field-trip` —
 * `/<namespace>/field-trip/wish/:id` — since that string also appears in the
 * path; pinning the position to index 2 excludes that case.
 */
const getPresentationModeFromPathname = (pathname: string): PresentationMode | null => {
  const segments = pathname.split('/').filter((s) => s.length > 0)
  const candidate = segments[2]
  return PRESENTATION_MODE_SEGMENTS.includes(candidate as PresentationMode) ? (candidate as PresentationMode) : null
}

export {
  buildPresentationLink,
  COVER_PAGE_INDEX,
  getCurrentPostCid,
  getPresentationModeFromPathname,
  getPresentationSearchParams,
  PINHOLE_HALF_DURATION_MS,
  resolvePageIndexFromUrl,
}
export type { PresentationMode, PresentationPage }
export { PresentationPageType }
